(() => {
  if (customElements.get('gg-coffee-story')) return;
  class CoffeeStory extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.audio = this.querySelector('audio');
      this.toggle = this.querySelector('[data-story-toggle]');
      this.label = this.querySelector('[data-story-label]');
      this.seek = this.querySelector('[data-story-seek]');
      this.status = this.querySelector('[data-story-status]');
      this.time = this.querySelector('[data-story-time]');
      this.transport = this.querySelector('.gg-coffee-story__transport');
      this.generation = 0;
      this.pending = false;
      if (!this.audio || !this.dataset.audioUrl) return;
      this.hidden = false;
      this.toggle.addEventListener('click', () => {
        if (this.pending || !this.audio.paused) this.stop();
        else this.start();
      }, options);
      this.querySelector('[data-story-restart]').addEventListener('click', () => {
        this.audio.currentTime = 0;
        this.start();
      }, options);
      this.seek.addEventListener('input', () => {
        if (Number.isFinite(this.audio.duration)) this.audio.currentTime = Number(this.seek.value);
        this.update();
      }, options);
      for (const event of ['loadedmetadata', 'durationchange', 'timeupdate', 'ended', 'pause']) {
        this.audio.addEventListener(event, () => this.update(), options);
      }
      this.audio.addEventListener('play', () => {
        document.querySelectorAll('audio, video').forEach(media => {
          if (media !== this.audio && !media.paused && !media.muted) media.pause();
        });
        document.dispatchEvent(new CustomEvent('gg:story-start', { detail: { player: this } }));
        this.update();
      }, options);
      document.addEventListener('play', event => {
        const media = event.target;
        if (media !== this.audio && media instanceof HTMLMediaElement && !media.muted) this.stop();
      }, { ...options, capture: true });
      this.audio.addEventListener('error', () => {
        this.stop();
        this.status.textContent = this.dataset.error;
      }, options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); }, options);
      window.addEventListener('pagehide', () => this.stop(), options);
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(this)) this.stop();
      }, options);
      this.setupPlacement(options);
    }
    setupPlacement(options) {
      if (!this.hasAttribute('data-story-mobile-description')) return;
      this.storyScope = this.closest('.product-information, .gg-gift-story');
      if (!this.storyScope) return;
      this.storyHome = document.createComment('gg-story-desktop-position');
      this.before(this.storyHome);
      this.storyQuery = window.matchMedia('(max-width: 749px)');
      this.storyQuery.addEventListener('change', () => this.placeStory(), options);
      this.placeStory();
    }
    placeStory() {
      if (!this.storyQuery || !this.isConnected || this.movingStory || !this.storyHome?.isConnected) return;
      let parent = this.storyHome.parentNode;
      let before = this.storyHome.nextSibling;
      if (this.storyQuery.matches) {
        const descriptions = this.storyScope.querySelectorAll('.product-details .ggx-product-description, .gg-gift-story__description');
        // Fail closed if the primary description is absent or ambiguous.
        if (descriptions.length !== 1) return;
        const description = descriptions[0];
        parent = description.parentNode;
        before = description.nextSibling;
      }
      if (before === this) return;
      // A resize must never interrupt a pending or playing recording. Apply the
      // new placement on the existing pause/ended update, keeping one audio node.
      if (this.pending || !this.audio.paused) return;
      const focus = this.contains(document.activeElement) ? document.activeElement : null;
      this.movingStory = true;
      try {
        parent.insertBefore(this, before);
      } finally {
        this.movingStory = false;
      }
      if (focus) focus.focus({ preventScroll: true });
    }
    start() {
      if (!this.isConnected || document.hidden) return;
      const generation = ++this.generation;
      this.pending = true;
      this.status.textContent = '';
      this.transport.hidden = false;
      if (!this.audio.getAttribute('src')) this.audio.src = this.dataset.audioUrl;
      if (this.audio.error) this.audio.load();
      if (this.audio.ended) this.audio.currentTime = 0;
      this.label.textContent = this.dataset.pause;
      let result;
      try { result = this.audio.play(); } catch (error) { this.failed(error, generation); return; }
      Promise.resolve(result).then(() => {
        if (generation !== this.generation || !this.isConnected || document.hidden) return;
        this.pending = false;
        this.update();
      }).catch(error => this.failed(error, generation));
    }
    failed(error, generation) {
      if (generation !== this.generation) return;
      this.pending = false;
      this.status.textContent = error.name === 'NotAllowedError' ? this.dataset.blocked : this.dataset.error;
      this.update();
    }
    stop() {
      this.generation++;
      this.pending = false;
      this.audio.pause();
      this.update();
    }
    update() {
      const duration = this.audio.duration;
      const format = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
      if (Number.isFinite(duration) && duration > 0) {
        this.seek.disabled = false;
        this.seek.max = String(duration);
        this.seek.value = String(this.audio.currentTime);
        this.seek.setAttribute('aria-valuetext', `${format(this.audio.currentTime)} / ${format(duration)}`);
        this.time.textContent = `${format(this.audio.currentTime)} / ${format(duration)}`;
      }
      this.toggle.setAttribute('aria-label', this.pending || !this.audio.paused ? this.dataset.pause : this.audio.ended ? this.dataset.replay : this.audio.currentTime > 0 ? this.dataset.resume : this.dataset.play);
      this.querySelector('.gg-coffee-story__symbol').textContent = this.pending || !this.audio.paused ? 'Ⅱ' : '▶';
      this.label.textContent = this.pending || !this.audio.paused ? this.dataset.pause : this.audio.ended ? this.dataset.replay : this.audio.currentTime > 0 ? this.dataset.resume : this.dataset.play;
      this.placeStory();
    }
    disconnectedCallback() {
      if (this.movingStory || !this.controller) return;
      this.storyHome?.remove();
      this.storyHome = null;
      this.storyScope = null;
      this.storyQuery = null;
      this.stop();
      this.controller.abort();
      this.controller = null;
    }
  }
  customElements.define('gg-coffee-story', CoffeeStory);
})();

